import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { authClient, signIn } from "../lib/auth-client";

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ssoMode, setSsoMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onPasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const { error } = await signIn.email({ email, password });
    setBusy(false);
    if (error) return setError(error.message ?? "Could not sign in.");
    navigate({ to: "/app" });
  }

  async function onGoogle() {
    setError(null);
    await signIn.social({ provider: "google", callbackURL: "/app" });
  }

  // Domain-based SSO: the user enters a work email, Better Auth matches it to
  // an org's registered OIDC provider and redirects to that IdP.
  async function onSsoSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const { error } = await authClient.signIn.sso({ email, callbackURL: "/app" });
    setBusy(false);
    if (error) {
      setError(
        error.message ?? "No single sign-on provider is configured for that email domain.",
      );
    }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-brand">
          Founder<span>Deck</span>
        </div>
        <div className="auth-sub">Plan your day, protect your focus.</div>

        {error && <div className="form-err">{error}</div>}

        {ssoMode ? (
          <form onSubmit={onSsoSubmit}>
            <div className="field">
              <label htmlFor="sso-email">Work email</label>
              <input
                id="sso-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </div>
            <button className="btn" type="submit" disabled={busy}>
              {busy ? "Redirecting…" : "Continue with SSO"}
            </button>
            <div className="auth-foot">
              <button
                type="button"
                className="linklike"
                onClick={() => {
                  setSsoMode(false);
                  setError(null);
                }}
              >
                ← Back to password sign-in
              </button>
            </div>
          </form>
        ) : (
          <>
            <form onSubmit={onPasswordSubmit}>
              <div className="field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <button className="btn" type="submit" disabled={busy}>
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>

            <div className="divider">or</div>

            <button className="btn-ghost" type="button" onClick={onGoogle}>
              Continue with Google
            </button>
            <button
              className="btn-ghost"
              type="button"
              style={{ marginTop: 8 }}
              onClick={() => {
                setSsoMode(true);
                setError(null);
              }}
            >
              Single sign-on (SSO)
            </button>

            <div className="auth-foot">
              <Link to="/forgot">Forgot password?</Link>
              <span> · </span>
              <Link to="/signup">Create an account</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
